import type { MaybeRef } from 'vue'
import { getSentryEnv } from '@/utils/sentry'
import * as Sentry from '@sentry/vue'
import { useRouter } from 'vue-router'
import { isRunningOnLocalHost } from '~/composables/tracking/tracking-scripts'

export function useSentry(
  isConsentGiven: MaybeRef<boolean>,
) {
  const isConsentGivenRef = ref(isConsentGiven)

  const router = useRouter()
  const { vueApp } = useNuxtApp()
  const config = useRuntimeConfig()

  /*
   * Initialize Sentry at onBeforeMount before rendering to get application context.
   Sample rate of 0 means 0% of errors are sent to make sure we don't send anything until consent was given.
   */
  Sentry.init({
    app: vueApp,
    dsn: 'https://36bf1cae3b424f8c441ef1fb4fc3b5cf@o88724.ingest.sentry.io/4506071576150016',
    enabled: false, // will be enabled after user gives consent
    environment: getSentryEnv() ? 'int' : 'prod',
    release: config.public.RELEASE_VERSION as string,
    sampleRate: 0.3,
    tracesSampleRate: 0.3,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        enableHTTPTimings: true,
        instrumentPageLoad: true,
        instrumentNavigation: true,
      }),
    ],
  })

  function startSentry() {
    if (Sentry.getClient() && !isRunningOnLocalHost())
      toggleSentry(true)
  }

  function stopSentry() {
    const onResolve = () => {
      // To be sure
      Sentry.captureException(
        new Error('CRITICAL: Sentry is sending data without user consent'),
      )
    }
    const onReject = () => {
      // Sth. went wrong, reload page to get fresh init
      location.reload()
    }
    toggleSentry(false)
    Sentry.close().then(onResolve, onReject)
  }

  function onConsentChanged() {
    if (isConsentGivenRef.value)
      startSentry()
    else
      stopSentry()
  }

  function toggleSentry(enabled: boolean) {
    const client = Sentry.getClient()
    if (client) {
      client.getOptions().enabled = enabled
      client.getOptions().sampleRate = enabled ? 1 : 0
      client.getOptions().tracesSampleRate = enabled ? 1 : 0
    }
  }

  onConsentChanged()

  watch(isConsentGivenRef, onConsentChanged)
}
